
import React from 'react';
import { useAdminPage } from '@/hooks/useAdminPage';
import AdminHeader from './AdminHeader';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { Skeleton } from '@/components/ui/skeleton';
import DatabaseErrorBoundary from './DatabaseErrorBoundary';
import AdminConnectivityMonitor from './AdminConnectivityMonitor';
import { ConnectionStatusStrict } from '@/hooks/useConnectionState';
import AdminFooter from './AdminFooter';
import { motion } from 'framer-motion';

interface AdminLayoutProps {
  children: React.ReactNode;
}

// Loading skeleton with motion
const LoadingSkeleton = () => (
  <div className="container mx-auto p-4 space-y-8">
    <Skeleton className="h-12 w-full max-w-md mb-8" />
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
      {[1, 2, 3].map((i) => (
        <motion.div 
          key={i}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: i * 0.1 }}
        >
          <Skeleton className="h-32 w-full" />
        </motion.div>
      ))}
    </div>
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5, delay: 0.4 }}
    >
      <Skeleton className="h-96 w-full" />
    </motion.div>
  </div>
);

const AdminLayout = ({ children }: AdminLayoutProps) => {
  const {
    lastSyncTime,
    adminName,
    isLoading,
    handleLogout,
    usingSupabase,
    connectionStatus,
    handleRetryConnection,
  } = useAdminPage();
  
  const headerSection = (
    <motion.div 
      className="mb-3"
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <AdminHeader 
        onLogout={handleLogout} 
        adminName={adminName} 
        lastSyncTime={lastSyncTime}
        connectionStatus={connectionStatus as ConnectionStatusStrict}
        retryConnection={handleRetryConnection}
      />
      
      <div className="flex justify-end mt-1.5">
        {!usingSupabase && connectionStatus === 'connected' && (
          <Badge 
            variant="warning"
            className="shadow-sm"
          >
            Lưu trữ cục bộ
          </Badge>
        )}
        
        {connectionStatus === 'connecting' && (
          <Badge 
            variant="info"
            className="shadow-sm"
          >
            Đang kết nối...
          </Badge>
        )}
      </div>
    </motion.div>
  );

  const connectionErrorAlert = (
    connectionStatus === 'disconnected' ? (
      <AdminConnectivityMonitor
        onRetryConnection={handleRetryConnection}
      />
    ) : null
  );
  
  const connectionLoadingAlert = (
    connectionStatus === 'connecting' ? (
      <Alert className="mb-4 border-blue-500 shadow-sm">
        <div className="flex items-center">
          <div className="mr-2 relative h-3 w-3">
            <div className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></div>
            <div className="relative inline-flex rounded-full h-3 w-3 bg-blue-500"></div>
          </div>
          <AlertTitle>Đang kết nối...</AlertTitle>
        </div>
        <AlertDescription>
          Đang thiết lập kết nối đến máy chủ. Vui lòng đợi trong giây lát...
        </AlertDescription>
      </Alert>
    ) : null
  );
  
  if (isLoading) {
    return <LoadingSkeleton />;
  }
  
  return (
    <div className="container mx-auto p-3 space-y-3 bg-slate-50 dark:bg-gray-900/95 min-h-screen flex flex-col">
      <div className="flex-grow">
        {headerSection}
        {connectionStatus === 'disconnected' && connectionErrorAlert}
        {connectionStatus === 'connecting' && connectionLoadingAlert}
        
        <DatabaseErrorBoundary
          onReset={handleRetryConnection}
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            {children}
          </motion.div>
        </DatabaseErrorBoundary>
      </div>
      
      <motion.div 
        className="mt-auto pt-4"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.3 }}
      >
        <AdminFooter />
      </motion.div>
    </div>
  );
};

export default AdminLayout;
